/**
 * Z_TPLAN - Transportplanung Page
 * React-Migration des ABAP Programms Z_TPLAN
 * 
 * Features:
 * - Bedarfsplanung anzeigen
 * - Verträge verwalten
 * - Transporte planen und zuweisen
 */

import { useState, useEffect } from 'react';
import { ZTPlanService, type Transport, type Demand, type ZTPlanData } from './ztplanService';
import './TransportPlanningPage.css';

interface FilterState {
    trandi: string;
    cw: string;
    year: string;
    frac: string;
    werks: string[];
}

export function TransportPlanningPage() {
    // Initial-Filter auf bekannten Testfall setzen: TPLST 3006, KW 40, Jahr 2025
    // Bei dieser Konstellation gibt es nur 10 Bedarfseinträge, keine Verträge/Transporte
    const [filter, setFilter] = useState<FilterState>({
        trandi: '3006',
        cw: '40',
        year: '2025',
        frac: '',
        werks: []
    });

    const [data, setData] = useState<ZTPlanData | null>(null);
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedDemand, setSelectedDemand] = useState<Demand | null>(null);
    const [selectedContract, setSelectedContract] = useState<string>('');
    const [selectedContractPos, setSelectedContractPos] = useState<string>('');
    const [selectedTransports, setSelectedTransports] = useState<Set<string>>(new Set());

    // Lade Daten beim Mounten und bei Filter-Änderungen
    useEffect(() => {
        console.log('[TransportPlanningPage] Filter changed:', filter);
        if (filter.trandi && filter.cw && filter.year) {
            console.log('[TransportPlanningPage] Loading data with valid filter');
            loadData();
        } else {
            console.log('[TransportPlanningPage] Skipping load - incomplete filter');
        }
    }, [filter.trandi, filter.cw, filter.year]);

    async function loadData() {
        console.log('[TransportPlanningPage] loadData started with filter:', filter);
        setLoading(true);
        setError(null);
        try {
            const result = await ZTPlanService.getData(filter);
            console.log('[TransportPlanningPage] Data loaded successfully:', {
                demandCount: result.demand.length,
                contractsCount: result.contracts.length,
                transportsCount: result.transports.length
            });
            setData(result);
        } catch (err) {
            console.error('[TransportPlanningPage] Error loading data:', err);
            setError(err instanceof Error ? err.message : 'Fehler beim Laden der Daten');
        } finally {
            setLoading(false);
        }
    }

    async function syncFromSAP() {
        console.log('[TransportPlanningPage] syncFromSAP called with filter:', filter);
        if (!filter.trandi || !filter.cw || !filter.year) {
            console.warn('[TransportPlanningPage] Sync cancelled - incomplete filter');
            alert('Bitte Transportdispostelle, Kalenderwoche und Jahr angeben');
            return;
        }

        setSyncing(true);
        setError(null);
        try {
            console.log('[TransportPlanningPage] Starting SAP sync...');
            await ZTPlanService.syncData(filter);
            console.log('[TransportPlanningPage] SAP sync completed, reloading data...');
            await loadData();
            console.log('[TransportPlanningPage] Sync and reload successful');
            alert('Daten erfolgreich aus SAP synchronisiert');
        } catch (err) {
            console.error('[TransportPlanningPage] Sync error:', err);
            setError(err instanceof Error ? err.message : 'Fehler bei der Synchronisation');
        } finally {
            setSyncing(false);
        }
    }

    async function showDebugInfo() {
        try {
            const response = await fetch(`/api/ztplan/debug?tenantId=68e43b18e0652b6ee86a59d6`, {
                headers: {
                    'Authorization': 'Bearer ' + localStorage.getItem('ia_access_token')
                }
            });
            const debugData = await response.json();
            console.log('[DEBUG] MongoDB Daten:', debugData);

            const message = `
=== MongoDB Debug Info ===

Dokument-Typen:
${debugData.typeCounts?.map((t: any) => `- ${t.type}: ${t.count} Dokumente`).join('\n') || 'Keine'}

Verfügbare Filter:
- Transportdispostellen: ${debugData.availableFilters?.transportdispostelle?.join(', ') || 'Keine'}
- Kalenderwochen: ${debugData.availableFilters?.kalenderwoche?.join(', ') || 'Keine'}
- Jahre: ${debugData.availableFilters?.jahr?.join(', ') || 'Keine'}

Beispiel-Bedarfe (bplan):
${debugData.bplanSamples?.length ? debugData.bplanSamples.map((b: any) => `- Trandi ${b.TRANSPORTDISPOSTELLE}, KW ${b.KALENDERWOCHE}/${b.JAHR}, Werk ${b.WERKS}, Fraktion ${b.FRAKTION}`).join('\n') : 'Keine gefunden'}
            `.trim();

            alert(message);
        } catch (err) {
            console.error('[DEBUG] Fehler:', err);
            alert('Fehler beim Abrufen der Debug-Info: ' + (err instanceof Error ? err.message : 'Unknown'));
        }
    }

    async function createTransport() {
        console.log('[TransportPlanningPage] createTransport called');
        if (!selectedDemand) {
            console.warn('[TransportPlanningPage] No demand selected');
            alert('Bitte einen Bedarf auswählen');
            return;
        }

        const menge = prompt('Anzahl Transporte:');
        if (!menge || isNaN(Number(menge))) {
            console.log('[TransportPlanningPage] Invalid quantity entered');
            return;
        }

        const transportData = {
            KALENDERWOCHE: filter.cw,
            JAHR: filter.year,
            WERKS: selectedDemand.WERKS,
            FRAKTION: selectedDemand.FRAKTION,
            BEDARF_ID: selectedDemand.BEDARF_ID,
            VERTRAG: selectedContract || undefined,
            VERTRAGSPOSITION: selectedContractPos || undefined,
            MENGE: Number(menge),
            GEPLANTE_MENGE: 0
        };
        console.log('[TransportPlanningPage] Creating transport with data:', transportData);

        try {
            await ZTPlanService.createTransport(transportData);
            console.log('[TransportPlanningPage] Transport created successfully');

            await loadData();
            alert('Transport erfolgreich erstellt');
        } catch (err) {
            console.error('[TransportPlanningPage] Error creating transport:', err);
            alert('Fehler beim Erstellen: ' + (err instanceof Error ? err.message : ''));
        }
    }

    async function deleteSelectedTransports() {
        console.log('[TransportPlanningPage] deleteSelectedTransports called, count:', selectedTransports.size);
        if (selectedTransports.size === 0) {
            console.warn('[TransportPlanningPage] No transports selected');
            alert('Bitte Transporte auswählen');
            return;
        }

        if (!confirm(`${selectedTransports.size} Transport(e) wirklich löschen?`)) {
            console.log('[TransportPlanningPage] Delete cancelled by user');
            return;
        }

        try {
            console.log('[TransportPlanningPage] Deleting transports:', Array.from(selectedTransports));
            for (const transportNum of selectedTransports) {
                console.log('[TransportPlanningPage] Deleting transport:', transportNum);
                await ZTPlanService.deleteTransport(transportNum);
            }
            console.log('[TransportPlanningPage] All transports deleted successfully');
            setSelectedTransports(new Set());
            await loadData();
            alert('Transporte erfolgreich gelöscht');
        } catch (err) {
            console.error('[TransportPlanningPage] Error deleting transports:', err);
            alert('Fehler beim Löschen: ' + (err instanceof Error ? err.message : ''));
        }
    }

    function toggleTransportSelection(transportNum: string) {
        const newSelection = new Set(selectedTransports);
        if (newSelection.has(transportNum)) {
            newSelection.delete(transportNum);
        } else {
            newSelection.add(transportNum);
        }
        setSelectedTransports(newSelection);
    }

    function getStatusIcon(transport: Transport): string {
        const status = ZTPlanService.getTransportStatus(transport);
        if (status === 'complete') return '🟢'; // Grün
        if (status === 'partial') return '🟡'; // Gelb
        return '🔴'; // Rot
    }

    return (
        <div className="transport-planning-page">
            <h1>Transportplanung (Z_TPLAN)</h1>

            {/* Filter Section */}
            <div className="filter-section">
                <div className="filter-row">
                    <div className="filter-field">
                        <label>Transportdispostelle *</label>
                        <input
                            type="text"
                            value={filter.trandi}
                            onChange={(e) => setFilter({ ...filter, trandi: e.target.value })}
                            placeholder="z.B. 1000"
                        />
                    </div>

                    <div className="filter-field">
                        <label>Kalenderwoche *</label>
                        <input
                            type="text"
                            value={filter.cw}
                            onChange={(e) => setFilter({ ...filter, cw: e.target.value })}
                            placeholder="KW"
                        />
                    </div>

                    <div className="filter-field">
                        <label>Jahr *</label>
                        <input
                            type="text"
                            value={filter.year}
                            onChange={(e) => setFilter({ ...filter, year: e.target.value })}
                            placeholder="YYYY"
                        />
                    </div>

                    <div className="filter-field">
                        <label>Fraktion</label>
                        <input
                            type="text"
                            value={filter.frac}
                            onChange={(e) => setFilter({ ...filter, frac: e.target.value })}
                            placeholder="optional"
                        />
                    </div>
                </div>

                <div className="action-buttons">
                    <button onClick={loadData} disabled={loading || !filter.trandi}>
                        {loading ? 'Lade...' : 'Aktualisieren'}
                    </button>
                    <button onClick={syncFromSAP} disabled={syncing || !filter.trandi}>
                        {syncing ? 'Synchronisiere...' : 'Aus SAP synchronisieren'}
                    </button>
                    <button onClick={showDebugInfo} style={{ backgroundColor: '#6c757d' }}>
                        🔍 Debug: Vorhandene Daten
                    </button>
                </div>
            </div>

            {error && <div className="error-message">❌ {error}</div>}

            {data && (
                <div className="data-section">
                    {/* Bedarf Table */}
                    <section className="data-panel">
                        <h2>Bedarf ({data.demand.length})</h2>
                        <div className="table-container">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>Bedarf-ID</th>
                                        <th>Werk</th>
                                        <th>TDST</th>
                                        <th>Fraktion</th>
                                        <th>Materialkurzttext</th>
                                        <th>LKW Lad.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.demand.map((d) => (
                                        <tr
                                            key={d.BEDARF_ID}
                                            className={selectedDemand?.BEDARF_ID === d.BEDARF_ID ? 'selected' : ''}
                                            onClick={() => setSelectedDemand(d)}
                                        >
                                            <td>
                                                <input
                                                    type="radio"
                                                    checked={selectedDemand?.BEDARF_ID === d.BEDARF_ID}
                                                    onChange={() => setSelectedDemand(d)}
                                                />
                                            </td>
                                            <td>{d.BEDARF_ID}</td>
                                            <td>{d.WERKS}</td>
                                            <td>{d.TRANSPORTDISPOSTELLE}</td>
                                            <td>{d.FRAKTION}</td>
                                            <td>{d.FRAKTION_BEZ}</td>
                                            <td>{d.MENGE}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>

                    {/* Verträge Table */}
                    <section className="data-panel">
                        <h2>Verträge ({data.contractPositions.length})</h2>
                        <div className="table-container">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>Vertrag</th>
                                        <th>Pos.</th>
                                        <th>Material</th>
                                        <th>Materialkurzttext</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.contractPositions.map((cp) => (
                                        <tr
                                            key={`${cp.VERTRAG}-${cp.VERTPOS}`}
                                            className={
                                                selectedContract === cp.VERTRAG && selectedContractPos === cp.VERTPOS
                                                    ? 'selected'
                                                    : ''
                                            }
                                            onClick={() => {
                                                setSelectedContract(cp.VERTRAG);
                                                setSelectedContractPos(cp.VERTPOS);
                                            }}
                                        >
                                            <td>
                                                <input
                                                    type="radio"
                                                    checked={
                                                        selectedContract === cp.VERTRAG && selectedContractPos === cp.VERTPOS
                                                    }
                                                    onChange={() => {
                                                        setSelectedContract(cp.VERTRAG);
                                                        setSelectedContractPos(cp.VERTPOS);
                                                    }}
                                                />
                                            </td>
                                            <td>{cp.VERTRAG}</td>
                                            <td>{cp.VERTPOS}</td>
                                            <td>{cp.MATNR}</td>
                                            <td>{cp.MAKTX || '-'}</td>
                                            <td>{cp.STATUS === ' ' ? 'Aktiv' : cp.STATUS}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>

                    {/* Transporte Table */}
                    <section className="data-panel">
                        <h2>Transporte ({data.transports.length})</h2>
                        <div className="action-buttons">
                            <button onClick={createTransport} disabled={!selectedDemand}>
                                Transport hinzufügen
                            </button>
                            <button onClick={deleteSelectedTransports} disabled={selectedTransports.size === 0}>
                                Ausgewählte löschen ({selectedTransports.size})
                            </button>
                        </div>
                        <div className="table-container">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th></th>
                                        <th>Status</th>
                                        <th>Transportnr.</th>
                                        <th>Werk</th>
                                        <th>Fraktion</th>
                                        <th>Fraktionsbezeichnung</th>
                                        <th>Spediteur</th>
                                        <th>Vertrag</th>
                                        <th>Pos</th>
                                        <th>Menge</th>
                                        <th>Gepl.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.transports.map((t) => (
                                        <tr
                                            key={t.TRANSPORTNUMMER}
                                            className={selectedTransports.has(t.TRANSPORTNUMMER) ? 'selected' : ''}
                                        >
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedTransports.has(t.TRANSPORTNUMMER)}
                                                    onChange={() => toggleTransportSelection(t.TRANSPORTNUMMER)}
                                                />
                                            </td>
                                            <td>{getStatusIcon(t)}</td>
                                            <td>{t.TRANSPORTNUMMER}</td>
                                            <td>{t.WERKS}</td>
                                            <td>{t.FRAKTION}</td>
                                            <td>{t.FRAKTION_BEZ || '-'}</td>
                                            <td>{t.SPED_BEZ || t.SPEDITEUR || '-'}</td>
                                            <td>{t.VERTRAG || '-'}</td>
                                            <td>{t.VERTRAGSPOSITION || '-'}</td>
                                            <td>{t.MENGE}</td>
                                            <td>{t.GEPLANTE_MENGE}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>
                </div>
            )}

            {!data && !loading && !error && (
                <div className="empty-state">
                    <p>Bitte Filter ausfüllen und Daten laden</p>
                </div>
            )}
        </div>
    );
}

// Helper function to get ISO week number
function getWeekNumber(date: Date): number {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}
